/**
 * Code promo pour la page /b/[slug] (événement).
 * Différence vs /a/[slug] : appel API events + s'applique sur tickets+options
 * (pas sur le total).
 */
import { eventsApi } from "~/services/api/events";
import type { TicketType } from "~/types/event";

export function useEventPromoCode(
  slug: Ref<string>,
  ticketTypes: Ref<TicketType[]>,
  ticketsOnlyPrice: Ref<number>,
  optionsTotal: Ref<number>,
) {
  const { t } = useI18n();
  const promoCode = ref("");
  const promoLoading = ref(false);
  const promoError = ref("");
  const promoApplied = ref<{ code: string; discount_type: string; discount_value: number } | null>(null);

  async function applyPromoCode() {
    if (!promoCode.value.trim() || promoLoading.value) return;
    promoLoading.value = true;
    promoError.value = "";
    try {
      const ticketTypeIds = ticketTypes.value.map(tt => tt.id);
      const result = await eventsApi.validatePromo(slug.value, promoCode.value.trim().toUpperCase(), ticketTypeIds);
      console.log("[Promo] Result:", result);
      if (result.valid) {
        promoApplied.value = {
          code: promoCode.value.trim().toUpperCase(),
          discount_type: result.discount_type!,
          discount_value: Number(result.discount_value || 0),
        };
      } else {
        promoError.value = result.message || t("billetterie.useEventPromoCode.codePromoInvalide");
      }
    } catch (e: unknown) {
      console.error("[Promo] Error:", e);
      promoError.value = (e as Error)?.message || t("billetterie.useEventPromoCode.codePromoInvalide");
    } finally {
      promoLoading.value = false;
    }
  }

  function removePromo() {
    promoApplied.value = null;
    promoCode.value = "";
    promoError.value = "";
  }

  const promoDiscount = computed(() => {
    if (!promoApplied.value) return 0;
    const subtotal = ticketsOnlyPrice.value + optionsTotal.value;
    if (promoApplied.value.discount_type === "percentage") {
      return Math.round((subtotal * promoApplied.value.discount_value / 100) * 100) / 100;
    }
    return Math.min(promoApplied.value.discount_value, subtotal);
  });

  return { promoCode, promoLoading, promoError, promoApplied, promoDiscount, applyPromoCode, removePromo };
}
